import apiService from './api';
import type {
  Announcement,
  AdminAnnouncement,
  PaginatedResponse,
} from '../types/ticket';

function extractData<T>(response: any): T {
  if (response.success && response.data !== undefined) {
    return response.data as T;
  }
  return response as T;
}

export const announcementService = {
  // ========================
  // User endpoints
  // ========================

  async getAnnouncements(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<Announcement>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', params.page.toString());
    if (params?.page_size) query.set('page_size', params.page_size.toString());
    const qs = query.toString();
    const res = await apiService.get<any>(`/tickets/announcements/${qs ? `?${qs}` : ''}`);
    return extractData<PaginatedResponse<Announcement>>(res);
  },

  async getUnreadAnnouncements(): Promise<Announcement[]> {
    const res = await apiService.get<any>('/tickets/announcements/unread/');
    return extractData<Announcement[]>(res);
  },

  async getAnnouncementUnreadCount(): Promise<number> {
    const res = await apiService.get<any>('/tickets/announcements/unread-count/');
    return extractData<{ count: number }>(res).count;
  },

  async getAnnouncement(id: string): Promise<Announcement> {
    const res = await apiService.get<any>(`/tickets/announcements/${id}/`);
    return extractData<Announcement>(res);
  },

  async markAnnouncementRead(id: string): Promise<void> {
    await apiService.post<any>(`/tickets/announcements/${id}/read/`, {});
  },

  // ========================
  // Admin endpoints
  // ========================

  async adminGetAnnouncements(params?: {
    type?: string;
    status?: string;
    priority?: string;
    search?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<AdminAnnouncement>> {
    const query = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== '') query.set(k, v.toString());
      });
    }
    const qs = query.toString();
    const res = await apiService.get<any>(`/tickets/admin/announcements/${qs ? `?${qs}` : ''}`);
    return extractData<PaginatedResponse<AdminAnnouncement>>(res);
  },

  async adminCreateAnnouncement(data: {
    title: string;
    content: string;
    type?: string;
    target_type?: string;
    target_level_min?: number | null;
    target_level_max?: number | null;
    priority?: string;
    is_pinned?: boolean;
    is_published?: boolean;
  }): Promise<AdminAnnouncement> {
    const res = await apiService.post<any>('/tickets/admin/announcements/create/', data);
    return extractData<AdminAnnouncement>(res);
  },

  async adminGetAnnouncement(id: string): Promise<AdminAnnouncement> {
    const res = await apiService.get<any>(`/tickets/admin/announcements/${id}/`);
    return extractData<AdminAnnouncement>(res);
  },

  async adminUpdateAnnouncement(id: string, data: {
    title?: string;
    content?: string;
    type?: string;
    target_type?: string;
    target_level_min?: number | null;
    target_level_max?: number | null;
    priority?: string;
    is_pinned?: boolean;
  }): Promise<AdminAnnouncement> {
    const res = await apiService.patch<any>(`/tickets/admin/announcements/${id}/update/`, data);
    return extractData<AdminAnnouncement>(res);
  },

  async adminDeleteAnnouncement(id: string): Promise<void> {
    await apiService.delete<any>(`/tickets/admin/announcements/${id}/delete/`);
  },

  async adminPublishAnnouncement(id: string): Promise<AdminAnnouncement> {
    const res = await apiService.post<any>(`/tickets/admin/announcements/${id}/publish/`, {});
    return extractData<AdminAnnouncement>(res);
  },

  async adminUnpublishAnnouncement(id: string): Promise<AdminAnnouncement> {
    const res = await apiService.post<any>(`/tickets/admin/announcements/${id}/unpublish/`, {});
    return extractData<AdminAnnouncement>(res);
  },
};
